import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getUserInfoAPI,
  getUserPermissionsAPI,
  loginAPI,
  logoutAPI,
} from '../api/auth.api';
import { useUserStore } from '../store/userStore';
import { Permission, User } from '../types/user';
import { clearPermissionCache } from '../utils/jwt';

/**
 * 인증 관련 기능을 제공하는 커스텀 훅
 */
export const useAuth = () => {
  const navigate = useNavigate();
  const { setUser, setAccessToken, clearUser } = useUserStore();

  /**
   * 권한 데이터를 표준 형식으로 변환
   *
   * 새로운 최적화된 API 응답 구조:
   * {
   *   user: {...},
   *   roles: ["developer", "admin"],  // 역할 이름 배열
   *   permissions: ["user:read", "user:create", ...]  // 권한 이름 배열
   * }
   */
  const transformPermissions = (permissionsData: any): Permission[] => {
    if (!permissionsData) return [];

    // 새로운 최적화된 API 구조 (permissions가 문자열 배열인 경우)
    if (
      permissionsData.permissions &&
      Array.isArray(permissionsData.permissions) &&
      typeof permissionsData.permissions[0] === 'string'
    ) {
      console.log('[useAuth] 새로운 최적화된 권한 구조 감지');
      return permissionsData.permissions.map((permName: string) => ({
        name: permName,
        displayName: permName,
        source: 'api',
      }));
    }

    // 레거시: 직접 permissions 배열이 있고 객체 형태인 경우
    if (
      permissionsData.permissions &&
      Array.isArray(permissionsData.permissions) &&
      typeof permissionsData.permissions[0] === 'object'
    ) {
      console.log('[useAuth] 레거시 권한 구조 (객체 배열) 감지');
      return permissionsData.permissions.map((perm: any) => ({
        name: perm.name,
        displayName: perm.displayName || perm.name,
        description: perm.description,
        source: perm.source,
        roleName: perm.roleName,
      }));
    }

    // 레거시: roles 내부에 permissions가 있는 경우
    if (permissionsData.roles && Array.isArray(permissionsData.roles)) {
      console.log('[useAuth] 레거시 권한 구조 (역할 내부) 감지');
      return permissionsData.roles.flatMap((role: any) =>
        Array.isArray(role.permissions)
          ? role.permissions.map((perm: any) => ({
              name: perm.name,
              displayName: perm.displayName || perm.name,
              description: perm.description,
              source: 'role',
              roleName: role.name,
            }))
          : []
      );
    }

    return [];
  };

  /**
   * 권한 정보를 로컬 스토리지에 저장
   */
  const savePermissionsToStorage = (permissions: Permission[]) => {
    const permissionNames = permissions.map((p) => p.name);
    localStorage.setItem(
      'lastUserPermissions',
      JSON.stringify(permissionNames)
    );
    console.log(`[useAuth] 권한 정보 저장됨: ${permissionNames.length}개`);
  };

  /**
   * 사용자 데이터 구성 및 설정
   */
  const setupUserData = async (
    userData: any,
    permissions: Permission[],
    rolesData?: any
  ) => {
    // roles 데이터 처리 (새로운 API는 roles를 permissionsData에서 전달)
    let roles = [];

    // rolesData가 별도로 전달된 경우 (새로운 최적화된 API)
    if (rolesData && Array.isArray(rolesData)) {
      if (typeof rolesData[0] === 'string') {
        // 문자열 배열인 경우 (새로운 API)
        roles = rolesData.map((roleName: string) => ({
          name: roleName,
          displayName: roleName,
        }));
      } else {
        // 객체 배열인 경우 (레거시)
        roles = rolesData.map((role: any) => ({
          _id: role._id,
          name: role.name,
          description: role.description,
        }));
      }
    }
    // userData에 roles가 있는 경우 (레거시)
    else if (userData.roles && Array.isArray(userData.roles)) {
      if (typeof userData.roles[0] === 'string') {
        // 문자열 배열
        roles = userData.roles.map((roleName: string) => ({
          name: roleName,
          displayName: roleName,
        }));
      } else {
        // 객체 배열
        roles = userData.roles.map((role: any) => ({
          _id: role._id,
          name: role.name,
          description: role.description,
        }));
      }
    }

    const user: User = {
      _id: userData._id,
      email: userData.email,
      name: userData.name,
      organizationType: userData.organizationType,
      department: userData.department,
      roles,
      permissions,
    };

    console.log('[useAuth] 사용자 설정:', {
      email: user.email,
      name: user.name,
      permissionsCount: permissions.length,
      rolesCount: roles.length,
    });

    setUser(user);
    savePermissionsToStorage(permissions);

    return user;
  };

  /**
   * 로그인 처리
   */
  const handleLogin = useCallback(
    async (email: string, password: string) => {
      try {
        console.log('[useAuth] 로그인 시도:', email);

        // 1. 로그인 API 호출
        const loginResponse = await loginAPI({ email, password });

        // API 응답 데이터 검증
        if (!loginResponse.data) {
          throw new Error('로그인 응답 데이터가 없습니다.');
        }

        const { accessToken, user } = loginResponse.data;

        if (!accessToken || !user) {
          throw new Error('로그인 응답 데이터가 올바르지 않습니다.');
        }

        console.log('[useAuth] 로그인 성공, 토큰 설정');

        // 2. 토큰 설정
        setAccessToken(accessToken);

        // 3. 권한 정보 조회
        console.log('[useAuth] 권한 정보 조회 중...');
        const permissionsData = await getUserPermissionsAPI();
        const permissions = transformPermissions(permissionsData);

        console.log('[useAuth] 권한 변환 완료:', permissions.length + '개');

        // 4. 사용자 데이터 설정 (roles도 permissionsData에서 가져옴)
        await setupUserData(user, permissions, permissionsData.roles);

        console.log('[useAuth] 로그인 완료 - 홈으로 이동');
        navigate('/');
      } catch (error) {
        console.error('[useAuth] 로그인 실패:', error);
        throw error;
      }
    },
    [navigate, setUser, setAccessToken]
  );

  /**
   * 로그아웃 처리
   */
  const handleLogout = useCallback(async () => {
    try {
      console.log('[useAuth] 로그아웃 시작...');

      // 서버 로그아웃 시도
      await logoutAPI();
      console.log('[useAuth] 서버 로그아웃 완료');
    } catch (error) {
      console.warn('[useAuth] 서버 로그아웃 실패:', error);
      // 서버 로그아웃 실패해도 클라이언트 정리는 계속
    } finally {
      // 클라이언트 상태 정리
      clearUser();
      clearPermissionCache();

      console.log('[useAuth] 로그아웃 완료 - 로그인 페이지로 이동');
      window.location.href = '/login';
    }
  }, [clearUser]);

  /**
   * 사용자 정보 조회 및 설정
   */
  const getUserInfo = useCallback(async () => {
    try {
      console.log('[useAuth] 사용자 정보 조회 시작...');

      // 1. 사용자 기본 정보 조회
      const userResponse = await getUserInfoAPI();
      if (!userResponse.data) {
        console.log('[useAuth] 사용자 정보 없음 - 로그아웃');
        await handleLogout();
        return;
      }

      console.log(
        '[useAuth] 사용자 기본 정보 조회 완료:',
        userResponse.data.email
      );

      // 2. 권한 정보 조회
      console.log('[useAuth] 권한 정보 조회 중...');
      const permissionsData = await getUserPermissionsAPI();
      const permissions = transformPermissions(permissionsData);

      console.log('[useAuth] 권한 조회 완료:', permissions.length + '개');

      // 3. 사용자 데이터 설정 (roles도 permissionsData에서 가져옴)
      await setupUserData(
        userResponse.data,
        permissions,
        permissionsData.roles
      );
    } catch (error) {
      console.error('[useAuth] 사용자 정보 조회 실패:', error);
      await handleLogout();
    }
  }, [setUser, handleLogout]);

  return {
    handleLogin,
    handleLogout,
    getUserInfo,
  };
};
